import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
const SOURCE = { automatico: "Aggiornamento notturno", manuale: "Aggiornamento manuale", excel: "Import Excel" };

export function ReteSyncPanel({ onSynced, refreshKey }) {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState("");

  const load = () => http.get("/rete/sync-log").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const syncNow = async () => {
    setBusy(true);
    setErr("");
    const before = logs[0]?.id;
    try {
      await http.post("/rete/sync-now");
      for (let i = 0; i < 30; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        const r = await http.get("/rete/sync-log");
        if (r.data[0]?.id !== before) { setLogs(r.data); break; }
      }
      onSynced?.();
    } catch {
      setErr("Aggiornamento non avviato: la sessione potrebbe essere scaduta. Ricarica la pagina e riprova.");
    } finally {
      setBusy(false);
    }
  };

  const last = logs[0];
  const lastOk = logs.find((l) => l.status === "ok");
  const issues = lastOk?.issues || [];
  const shown = showAll ? issues : issues.slice(0, 8);

  return (
    <div className="rounded-2xl bg-lilla/40 border border-viola/15 p-5" data-testid="rete-sync-panel">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:justify-between">
        <div>
          <p className="font-display text-lg">Sincronizzazione Google Sheet</p>
          <p className="text-sm text-viola/75 mt-1">Ogni notte alle 3:00 la Rete si aggiorna da sola dal foglio. Il foglio vince sempre sulle modifiche fatte qui.</p>
        </div>
        <button type="button" onClick={syncNow} disabled={busy} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-viola text-cream px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="rete-sync-now">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} aria-hidden /> {busy ? "Aggiornamento…" : "Aggiorna ora dal foglio"}
        </button>
      </div>

      {err && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="rete-sync-request-error">{err}</p>}
      {last ? (
        <div className="mt-4 text-sm" data-testid="rete-sync-last">
          {last.status === "ok" ? (
            <p className="flex items-center gap-2"><CheckCircle2 size={16} className="text-green-700" aria-hidden />
              {SOURCE[last.source] || last.source} del {fmt(last.at)}: {last.inserted} schede, {last.duplicates_removed} doppioni uniti.</p>
          ) : (
            <p className="flex items-start gap-2 text-red-700" data-testid="rete-sync-error"><AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
              {SOURCE[last.source] || last.source} del {fmt(last.at)} non riuscito: {last.error} La Rete non è stata modificata.</p>
          )}
          {issues.length > 0 && (
            <div className="mt-3 rounded-xl bg-white/70 border border-arancio/50 p-4" data-testid="rete-sync-issues">
              <p className="font-semibold flex items-center gap-2"><AlertTriangle size={15} className="text-fucsia" aria-hidden /> Da controllare ({issues.length})</p>
              {lastOk !== last && <p className="text-xs text-viola/70 mt-1">Dall'ultimo aggiornamento riuscito ({fmt(lastOk.at)}).</p>}
              <ul className="mt-2 space-y-1.5">
                {shown.map((it, i) => (
                  <li key={i} className="text-sm" data-testid={`rete-sync-issue-${i}`}><strong>{it.name}</strong>: {it.problem}</li>
                ))}
              </ul>
              {issues.length > 8 && (
                <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-2 text-xs font-semibold text-fucsia hover:underline" data-testid="rete-sync-issues-toggle">
                  {showAll ? "Mostra meno" : `Mostra tutti (${issues.length})`}
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="mt-4 text-sm text-viola/70">Nessun aggiornamento registrato finora.</p>
      )}
    </div>
  );
}
